import { ImageName } from "@mcleod/images";
import { Button, Layout, SelectionType, SlideoutDecorator, Snackbar, Table, TableRow, TableRowDisplayEvent } from "@mcleod/components";
import { Api, ServerError } from "@mcleod/core";
import { AutogenLayoutPartialShipmentManifestCreationSlideout } from "./autogen/AutogenLayoutPartialShipmentManifestCreationSlideout";
import { PartialShipmentManifestDetails } from "./PartialShipmentManifestDetails";
import { CommonDialogs } from "@mcleod/common";

interface PartialShipmentManifestCreationSlideoutProps {
    movement_ids: string[];
    selectedRows?: TableRow[];
    doAfterSlideOut?: (success: boolean, manifestId: string) => void;
    table?: Table;
}

export class PartialShipmentManifestCreationSlideout extends AutogenLayoutPartialShipmentManifestCreationSlideout {

    private _success: boolean = false;
    private _manifestId: string = "";
    public get manifestId(): string {return this._manifestId;}
    public set manifestId(value: string) {this._manifestId = value;}
    public get success(): boolean {return this._success;}
    public set success(value: boolean) {this._success = value;}

    static showSlideout(props: PartialShipmentManifestCreationSlideoutProps) {
        const layout = Layout.getLayout("lme/dispatch/PartialShipmentManifestCreationSlideout") as PartialShipmentManifestCreationSlideout;
        const movementIDs = props.movement_ids;
        const hasMovements: boolean = movementIDs.length > 0;
        const orderTable = props.table;
        const slideoutDecorator = new SlideoutDecorator({
            layout: layout,
            width: "75%",
            onClose: (cancelled: boolean) => layout.slideOut(),
            fillVerticalSpace: true,
            title: hasMovements ? "Create Manifest": "Create Shell Manifest",
            overlayProps: { closeOnClickOff: false, greyedBackground: true},
            addlComponents: new Button({
                id: "dispatchSaveButton",
                caption: "Create",
                backgroundColor: "primary",
                color: "default.reverse",
                minWidth: 128,
                borderWidth: 0,
                rowBreak: false,
                onClick: (event) => saveManifest(event.target as Button, layout as PartialShipmentManifestCreationSlideout, props)
            }),
            layoutLoadListeners: (() => {
                const layoutManifestDetails = layout.layoutPartialShipmentManifestDetails as PartialShipmentManifestDetails;
                const tools = layoutManifestDetails.tabsetManifestDetails.afterTools;
                tools.forEach((tool) => { tool.visible = false; });
                if (movementIDs.length > 0) {
                    layoutManifestDetails.sourcePartialShipmentManifestOrderDetails.search({ movement_ids: props.movement_ids });
                    layoutManifestDetails.sourcePartialShipmentManifestStopDetails.search({ movement_ids: props.movement_ids, operation: "create" });
                    layoutManifestDetails.tableStopTables.addRowDisplayListener( (event: TableRowDisplayEvent) => {
                        const tableRow = event.getTableRow();
                        const orderId = tableRow?.data?.get("stop.order_id");
                        tableRow.contextMenuItems = [
                            {
                                imageName: ImageName.delete,
                                text: `Remove ${orderId} from Manifest`,
                                onClick: () => layout.removeOrder(tableRow, layout, slideoutDecorator, props.selectedRows, orderTable)
                            }
                        ];
                    });
                }
            }),
            doAfterSlideOut: () => {
                props.doAfterSlideOut(layout.success, layout.manifestId);
            }
        });
    }

    removeOrder(tableRow: TableRow, layout: PartialShipmentManifestCreationSlideout, sod: SlideoutDecorator, selectedRows: TableRow[], orderTable: Table): void {
        const movementId = tableRow.table.id === "tableStopTables" ? tableRow.data.get("id") : tableRow.data.get("movement_id");
        const manifestDetailsLayout = layout.layoutPartialShipmentManifestDetails;
        if (selectedRows) {
            selectedRows.forEach((row) => {
                if (row.data.get("movement_id") === movementId) {
                    row.backgroundColor = null;
                    orderTable.selectRow(row, SelectionType.DISTINCT, null);
                }
            });
        }
        const movementIDs = [];
        orderTable.selectedRows.forEach((row) => {
            if (!movementIDs.includes(row.data.get("movement_id"))) {
                movementIDs.push(row.data.get("movement_id"));
            }
        })
        if (movementIDs.length !== 0 ) {
            manifestDetailsLayout.sourcePartialShipmentManifestOrderDetails.search({ movement_ids: movementIDs });
            manifestDetailsLayout.sourcePartialShipmentManifestStopDetails.search({ movement_ids: movementIDs });
        }
        else {
            const orderDetails = manifestDetailsLayout.tableOrderDetails as Table;
            const stopDetails = manifestDetailsLayout.tableStopTables as Table;
            orderDetails.clear();
            stopDetails.clear();
            sod.labelTitle.text = "Create Shell Manifest";
        }
    }
}

async function saveManifest(button: Button, layout: PartialShipmentManifestCreationSlideout, props: PartialShipmentManifestCreationSlideoutProps): Promise<void> {
    button.busy = true;
    const layoutManifestDetails = layout.layoutPartialShipmentManifestDetails;
    const hasStopsChanged: boolean = layoutManifestDetails.sourcePartialShipmentManifestStopDetails.hasChanged();
    const stops = [];
    const stopRows = layoutManifestDetails.tableStopTables.rows;
    if (hasStopsChanged) {
        if (!layoutManifestDetails.stopsValidated(stopRows)) {
            button.busy = false;
            return;
        }
        // Need to loop  through each row and update movement sequence. Original code does not match up.
        stopRows.forEach((row, index) => {
            row.data.set("stop.movement_sequence", index + 1);
            stops.push({stop_id: row.data.get("stop.id"), movement_sequence: index + 1});
        });
    }
    const movementIds = props.movement_ids?.length > 0 ? props.movement_ids : null;
    const description = layout.textboxDescription.getDataValue();
    const input = { "movements": movementIds, "description": description, stops: stops };
    try {
        const response = await Api.post("lme/dispatch/partial-shipment-planning-create-manifest", input);
        if (response.data[0]?.success === true) {
            layout.manifestId = response.data[0]?.manifest_id;
            layout.success = true;
            layout.slideOut();
        } else {
            layout.success = false;
            Snackbar.showWarningSnackbar("Manifest creation failed. Please try again.");
        }
    } catch (error) {
        if (error instanceof ServerError) {
                CommonDialogs.showError(error, "Error Creating Manifest");
        } else {
            Snackbar.showWarningSnackbar(error, { persist: true });
        }
    } finally {
        button.busy = false;
    }
}
